import React, { useState, useEffect, useRef, useCallback } from 'react';
import { theme } from '../theme';
import { useLanguage } from '../i18n/useLanguage';

/* ── Props ─────────────────────────────────────────────── */
interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string, reportName: string) => Promise<void>;
}

type ModalState = 'form' | 'loading' | 'success' | 'error';

/* ── Email validation (RFC 5322 simplified) ────────────── */
const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export function validateEmail(value: string): boolean {
  return EMAIL_RE.test(value);
}

/* ── Component ─────────────────────────────────────────── */
export const EmailModal: React.FC<EmailModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useLanguage();

  const [reportName, setReportName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState(false);
  const [modalState, setModalState] = useState<ModalState>('form');
  const [errorMsg, setErrorMsg] = useState('');

  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const emailValid = validateEmail(email);
  const canSubmit = emailValid && consent;

  /* ── Reset on open ───────────────────────────────────── */
  useEffect(() => {
    if (isOpen) {
      setReportName('');
      setEmail('');
      setConsent(false);
      setTouched(false);
      setModalState('form');
      setErrorMsg('');
    }
  }, [isOpen]);

  /* ── Focus management ────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      if (modalState === 'form') emailInputRef.current?.focus();
      else closeButtonRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen, modalState]);

  /* ── Escape key ──────────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  /* ── Focus trap ──────────────────────────────────────── */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    []
  );

  /* ── Submit handler ──────────────────────────────────── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setModalState('loading');
    try {
      await onSubmit(email, reportName);
      setModalState('success');
    } catch (err: unknown) {
      setErrorMsg(
        err instanceof Error ? err.message : t('modal.error')
      );
      setModalState('error');
    }
  };

  /* ── Click outside ───────────────────────────────────── */
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  if (!isOpen) return null;

  /* ── Styles ──────────────────────────────────────────── */
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
  };

  const dialog: React.CSSProperties = {
    background: theme.cardBg,
    border: `1px solid ${theme.border}`,
    borderRadius: 12, padding: 32,
    maxWidth: 480, width: '90%',
    maxHeight: '90vh', overflowY: 'auto',
    position: 'relative',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px',
    border: `1px solid ${touched && !emailValid ? '#d32f2f' : theme.border}`,
    borderRadius: 6, fontSize: 15,
    outline: 'none', boxSizing: 'border-box',
  };

  const btnPrimary: React.CSSProperties = {
    width: '100%', padding: '12px 0',
    background: canSubmit ? theme.primary : '#ccc',
    color: '#fff', fontWeight: 700, fontSize: 15,
    border: 'none', borderRadius: 6,
    cursor: canSubmit ? 'pointer' : 'not-allowed',
    opacity: canSubmit ? 1 : 0.7,
  };

  const btnSecondary: React.CSSProperties = {
    padding: '10px 20px',
    background: theme.primarySoft,
    color: theme.primary, fontWeight: 600,
    border: `1px solid ${theme.primaryBorder}`,
    borderRadius: 6, cursor: 'pointer',
  };

  /* ── Render ──────────────────────────────────────────── */
  return (
    <div ref={overlayRef} style={overlay} onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-modal-title"
        onKeyDown={handleKeyDown}
        style={dialog}
      >
        {/* ── FORM state ─────────────────────────────── */}
        {modalState === 'form' && (
          <form onSubmit={handleSubmit} noValidate>
            <h2 id="email-modal-title" style={{ margin: '0 0 20px', fontSize: 20, color: theme.text }}>
              {t('modal.title')}
            </h2>

            {/* Report Name input */}
            <label htmlFor="report-name-input" style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: theme.text }}>
              {t('modal.reportNameLabel')}
            </label>
            <input
              id="report-name-input"
              type="text"
              value={reportName}
              placeholder={t('modal.reportNamePlaceholder')}
              onChange={(e) => setReportName(e.target.value)}
              style={{ ...inputStyle, marginBottom: 16, border: `1px solid ${theme.border}` }}
            />

            {/* Email input */}
            <label htmlFor="email-input" style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: theme.text }}>
              {t('modal.emailLabel')}
            </label>
            <input
              ref={emailInputRef}
              id="email-input"
              type="email"
              value={email}
              placeholder={t('modal.emailPlaceholder')}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              style={inputStyle}
              autoComplete="email"
            />
            {touched && !emailValid && (
              <p style={{ color: '#d32f2f', fontSize: 13, margin: '4px 0 0' }} role="alert">
                {t('modal.emailError')}
              </p>
            )}

            {/* Consent checkbox */}
            <label style={{ display: 'flex', gap: 10, marginTop: 20, cursor: 'pointer', fontSize: 13, lineHeight: 1.5, color: theme.text }}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <span>{t('modal.consent')}</span>
            </label>

            {/* Privacy notice */}
            <p style={{ fontSize: 12, color: theme.muted, marginTop: 16, lineHeight: 1.6 }}>
              {t('modal.privacyNotice')}{' '}
              <a href="/datenschutz.html" target="_blank" rel="noopener noreferrer" style={{ color: theme.accent, textDecoration: 'underline' }}>
                {t('modal.privacyLink')}
              </a>
            </p>

            {/* Submit */}
            <button type="submit" disabled={!canSubmit} style={btnPrimary}>
              {t('modal.submit')}
            </button>

            {/* Cancel */}
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', marginTop: 10 }}>
              {t('modal.close')}
            </button>
          </form>
        )}

        {/* ── LOADING state ──────────────────────────── */}
        {modalState === 'loading' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontSize: 16, color: theme.text }}>{t('modal.loading')}</p>
          </div>
        )}

        {/* ── SUCCESS state ──────────────────────────── */}
        {modalState === 'success' && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p style={{ fontSize: 24, marginBottom: 12 }}>✅</p>
            <h2 style={{ fontSize: 18, color: theme.text, marginBottom: 8 }}>{t('modal.success')}</h2>
            <p style={{ color: theme.muted, marginBottom: 24 }}>
              {t('modal.successDetail', { email })}
            </p>
            <button ref={closeButtonRef} onClick={onClose} style={btnSecondary}>
              {t('modal.close')}
            </button>
          </div>
        )}

        {/* ── ERROR state ────────────────────────────── */}
        {modalState === 'error' && (
          <div style={{ padding: '24px 0' }}>
            <p style={{ fontSize: 24, textAlign: 'center', marginBottom: 12 }}>⚠️</p>
            <h2 style={{ fontSize: 18, color: theme.text, textAlign: 'center', marginBottom: 8 }}>
              {t('modal.error')}
            </h2>
            <p style={{ color: '#d32f2f', textAlign: 'center', marginBottom: 24, fontSize: 14 }}>
              {errorMsg}
            </p>
            <button
              ref={closeButtonRef}
              onClick={() => setModalState('form')}
              style={{ ...btnPrimary, background: theme.primary, cursor: 'pointer', opacity: 1 }}
            >
              {t('modal.retry')}
            </button>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', marginTop: 10 }}>
              {t('modal.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
